import { createContext, useContext, useState } from "react";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faChevronDown,
  faChevronRight,
  faDiagramNested,
} from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence, m } from "framer-motion";
import {
  NavLink,
  type NavLinkProps,
  type To,
  useMatch,
} from "react-router-dom";
import { twJoin } from "tailwind-merge";

import RetoolIcon from "src/assets/RetoolMenuIcon.svg?react";
import { Icon, type IconSize } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { FolderList } from "src/flowsOverview/v2/FolderList";
import { useFolders } from "src/flowsOverview/v2/folderQueries";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { getUrlToWsDashboard, WsDashboardPageCode } from "src/router/urls";
import { useCurrentPage } from "src/router/useCurrentPage";

type CollapsedContextProps = {
  collapsed: boolean;
  isDragHovering: boolean;
};
const CollapsedContext = createContext<CollapsedContextProps | undefined>(
  undefined,
);
const useCollapsed = (): CollapsedContextProps => {
  const context = useContext(CollapsedContext);
  if (!context) {
    throw new Error(
      `The NavSideBar collapsed context must be used within a NavSideBar`,
    );
  }
  return context;
};

type CustomNavLinkProps = NavLinkProps & {
  disabled?: boolean;
};

export type RetoolNavItemProps = {
  dataLoc: string;
  retoolPageUrl: string;
  retoolConnectionName: string;
  isCurrentPageRetool: boolean;
};

export const RetoolNavItem: React.FC<RetoolNavItemProps> = ({
  dataLoc,
  retoolPageUrl,
  retoolConnectionName,
  isCurrentPageRetool,
}) => {
  const { collapsed } = useCollapsed();
  return (
    <Tooltip
      body={retoolConnectionName}
      disabled={!collapsed}
      placement="right"
      title={collapsed ? "Case Management" : undefined}
      asChild
    >
      <li className="shrink-0 cursor-default">
        <CustomNavLink
          className={twJoin(
            "mr-2.5 flex items-center gap-x-2 rounded-lg px-2 py-1.5",
            isCurrentPageRetool ? "bg-gray-100" : "",
            "hover:bg-gray-50",
          )}
          data-loc={dataLoc}
          end={false}
          to={retoolPageUrl}
        >
          <RetoolIcon className="m-1 h-3 w-3" />
          {!collapsed && (
            <span className="font-inter-medium-13px text-gray-800">
              Case Management
            </span>
          )}
        </CustomNavLink>
      </li>
    </Tooltip>
  );
};

const CustomNavLink: React.FC<CustomNavLinkProps> = ({ disabled, ...rest }) => {
  if (disabled) {
    const disabledState = {
      isActive: false,
      isPending: false,
      isTransitioning: false,
    } as const;
    return (
      <span
        className={
          typeof rest.className === "function"
            ? rest.className(disabledState)
            : rest.className
        }
      >
        {typeof rest.children === "function"
          ? rest.children(disabledState)
          : rest.children}
      </span>
    );
  }

  return <NavLink {...rest} />;
};

type NavItemProps = {
  title: string;
  message?: string;
  to: To;
  icon: IconProp;
  disabled?: boolean;
  dataLoc?: string;
  neverShowActive?: boolean;
  end?: boolean;
  isActiveOverride?: boolean;
  iconSize?: IconSize;
  externalLink?: boolean;
};

export const NavItem: React.FC<NavItemProps> = ({
  disabled,
  dataLoc,
  title,
  message,
  to,
  icon,
  iconSize = "xs",
  neverShowActive = false,
  end = true,
  isActiveOverride,
  externalLink = false,
}) => {
  const { collapsed } = useCollapsed();
  const linkContent = (
    <>
      <Icon
        icon={icon}
        size={iconSize}
        variant={disabled ? "disabled" : "default"}
      />
      {!collapsed && (
        <span
          className={twJoin(
            "font-inter-medium-13px",
            disabled ? "pointer-events-none text-gray-400" : "text-gray-800",
          )}
        >
          {title}
        </span>
      )}
    </>
  );
  return (
    <Tooltip
      body={message}
      disabled={!(collapsed || Boolean(message))}
      placement="right"
      title={collapsed ? title : undefined}
      asChild
    >
      <li className="shrink-0 cursor-default">
        {externalLink ? (
          <a
            href={to as string}
            target="_blank"
            rel="noopener noreferrer"
            className="mr-2.5 flex items-center gap-x-2 rounded-lg p-1.5 hover:bg-gray-50"
          >
            {linkContent}
          </a>
        ) : (
          <CustomNavLink
            className={({ isActive }) =>
              twJoin(
                "mr-2.5 flex items-center gap-x-2 rounded-lg p-1.5",
                (isActiveOverride || (isActive && !neverShowActive)) &&
                  "bg-gray-100",
                disabled ? "cursor-default" : "hover:bg-gray-50",
              )
            }
            data-loc={dataLoc}
            disabled={disabled}
            end={end}
            to={to}
          >
            {linkContent}
          </CustomNavLink>
        )}
      </li>
    </Tooltip>
  );
};

export const DecisionFlowsNavItem: React.FC<{
  orgId: string;
  wsId: string;
}> = ({ orgId, wsId }) => {
  const { isAuthoringPage } = useCurrentPage();
  const { collapsed, isDragHovering } = useCollapsed();
  const [foldersVisible, setFoldersVisible] = useState(false);

  const isOnFlowsPage = Boolean(
    useMatch({ path: "/org/:orgId/ws/:wsId/flows", end: false }),
  );
  // Show the `FolderList` automatically when navigating to the flows page.
  const [prevIsOnFlowsPage, setPrevIsOnFlowsPage] = useState(false);
  if (isOnFlowsPage && !prevIsOnFlowsPage) {
    setPrevIsOnFlowsPage(true);
    setFoldersVisible(true);
  } else if (!isOnFlowsPage && prevIsOnFlowsPage) {
    setPrevIsOnFlowsPage(false);
  }

  const foldersResult = useFolders({ workspaceId: wsId });
  const handleFoldersVisibleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setFoldersVisible(!foldersVisible);
  };

  return (
    <Tooltip
      disabled={!collapsed}
      placement="right"
      title={collapsed ? "Decision Flows" : undefined}
      asChild
    >
      <li
        className={twJoin(
          "flex max-h-124 shrink cursor-default flex-col overflow-x-visible",
          (foldersVisible || isDragHovering) &&
            !collapsed &&
            isFeatureFlagEnabled(FEATURE_FLAGS.informationArchitecture)
            ? "min-h-[12rem]"
            : "",
        )}
      >
        <CustomNavLink
          className={({ isActive }) =>
            twJoin(
              "mr-2.5 flex shrink-0 items-center gap-x-2 rounded-lg",
              collapsed ? "p-1.5" : "py-1.5 pr-2 pl-1.5",
              (isAuthoringPage || isActive) && "bg-gray-100",
              "hover:bg-gray-50",
            )
          }
          data-loc="decision-flows-icon"
          end={false}
          to={getUrlToWsDashboard({
            orgId,
            wsId,
            page: WsDashboardPageCode.Flows,
          })}
        >
          <Icon icon={faDiagramNested} size="xs" variant="default" />
          {!collapsed && (
            <>
              <span className="w-full font-inter-medium-13px text-gray-800">
                Decision Flows
              </span>
              {isFeatureFlagEnabled(FEATURE_FLAGS.informationArchitecture) && (
                <div onClick={handleFoldersVisibleClick}>
                  <Icon
                    icon={foldersVisible ? faChevronDown : faChevronRight}
                    size="xs"
                  />
                </div>
              )}
            </>
          )}
        </CustomNavLink>
        {!collapsed &&
          isFeatureFlagEnabled(FEATURE_FLAGS.informationArchitecture) && (
            <AnimatePresence initial={false}>
              {(foldersVisible || isDragHovering) && (
                <m.div
                  key="folder-list"
                  animate={{ height: "auto", opacity: 1 }}
                  className="max-h-128 overflow-hidden pt-2"
                  exit={{ height: 0, opacity: 0 }}
                  initial={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <FolderList
                    folderResult={foldersResult}
                    organizationId={orgId}
                    variant="sidebar"
                    workspaceId={wsId}
                  />
                </m.div>
              )}
            </AnimatePresence>
          )}
      </li>
    </Tooltip>
  );
};

type NavSidebarProps = {
  collapsed: boolean;
  isDragHovering?: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>> | null;
  children: React.ReactNode;
  wide?: boolean;
};
export const NavSidebar: React.FC<NavSidebarProps> = ({
  collapsed,
  isDragHovering,
  setCollapsed,
  children,
  wide = false,
}) => {
  return (
    <aside
      className={twJoin(
        "z-[1] h-full bg-white py-3.5 shadow-base",
        !collapsed && "flex-shrink-0 pl-2.5",
        !collapsed && wide && "w-60",
        !collapsed && !wide && "w-52",
        collapsed && "pl-2.5",
      )}
    >
      <nav className="flex h-full flex-col" data-loc="flow-subnavigation">
        <CollapsedContext.Provider
          value={{ collapsed, isDragHovering: isDragHovering ?? false }}
        >
          <ul className="decideScrollbarThin flex min-h-0 shrink flex-col gap-y-2 overflow-y-auto">
            {children}
          </ul>
          <div
            className="min-h-[48px] shrink-0 grow"
            onClick={() => setCollapsed?.((prev) => !prev)}
          />
        </CollapsedContext.Provider>
      </nav>
    </aside>
  );
};
